import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Search, Filter, ChevronDown, ChevronUp, Eye, Download, X } from 'lucide-react';
import { fetchCallFilterOptions, fetchCalls, getTaskAudioUrl } from '../api';
import { useExportJobs } from './exports/ExportJobsContext';
import { getTexts } from '../utils/configLoader';
import useEmployees from '../hooks/useEmployees';
import { CALL_TYPE_LABELS, FUNNEL_STAGE_LABELS, getCallTypeOptionsForTenant } from '../config/endoTaxonomy';
import { formatClassificationType } from '../utils/callClassification';
import { formatNegativeCategory, formatNegativeSubCategory } from '../utils/negativeLabels';

const PAGE_SIZE = 25;
const TENANT_ID = process.env.REACT_APP_TENANT_ID?.trim().toLowerCase();
const IS_PLUS_TENANT = TENANT_ID === 'plus';
const ALTERNATIVE_OFFER_OPTIONS = [
  { code: 'all', label: 'Альтернативный товар: все' },
  { code: 'yes', label: 'Альтернативный товар: да' },
  { code: 'no', label: 'Альтернативный товар: нет' }
];


const NEGATIVE_SUB_CATEGORY_LABELS = {
  safety_issue: 'Риск безопасности/поломки',
  repeated_unresolved: 'Повторно не решено',
  no_clear_next_step: 'Нет ясного следующего шага',
  low_quality_call: 'Низкое качество звонка',
  no_response_or_followup: 'Нет ответа или продолжения',
  room_or_facility_problem: 'Проблема в номере / корпусе',
  medical_or_procedure_service: 'Медицинский / процедурный сервис',
  booking_or_availability: 'Бронь, места или расписание',
  price_or_documents: 'Цена или документы',
  staff_communication: 'Коммуникация сотрудников',
  other: 'Другая жалоба'
};

const CRITERION_LABELS = {
  answered_question: 'Ответил на вопрос клиента',
  identified_need: 'Выявил потребность',
  fixed_next_step: 'Зафиксировал следующий шаг',
  clear_closing: 'Корректно завершил разговор',
  no_forbidden_phrases: 'Не использовал запрещенные фразы',
  found_order_or_context: 'Нашел заявку/контекст',
  communicated_status_or_time: 'Сообщил статус или сроки',
  confirmed_agreements: 'Подтвердил договоренности',
  clarified_problem: 'Уточнил проблему',
  gave_instruction_or_solution: 'Дал инструкцию или решение',
  offered_service_next_step: 'Предложил сервисный следующий шаг',
  no_internal_problem: 'Не раскрывал внутренние проблемы'
};

const CallDetails = ({
  dateRange,
  initialCallId,
  onInitialCallHandled,
  initialFilters,
  onInitialFiltersHandled,
  globalCallType = 'all',
  onGlobalCallTypeChange
}) => {
  const texts = getTexts('calls');
  const { employees } = useEmployees();
  const { startExport } = useExportJobs();

  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState(() => initialFilters?.search ?? '');
  const [selectedDepartment, setSelectedDepartment] = useState(() => initialFilters?.department ?? 'all');
  const [selectedEmployee, setSelectedEmployee] = useState(() => initialFilters?.employee_id ?? 'all');
  const [selectedClassificationType, setSelectedClassificationType] = useState(() => initialFilters?.call_classification_type ?? 'all');
  const [selectedCallType, setSelectedCallType] = useState(() => initialFilters?.call_type ?? globalCallType ?? 'all');
  const [selectedAlternativeOffer, setSelectedAlternativeOffer] = useState(() => initialFilters?.alternative_offered ?? 'all');
  const [selectedPrimaryIntent, setSelectedPrimaryIntent] = useState(() => initialFilters?.primary_intent ?? 'all');
  const [selectedServiceArea, setSelectedServiceArea] = useState(() => initialFilters?.service_area ?? 'all');
  const [selectedBookingOutcome, setSelectedBookingOutcome] = useState(() => initialFilters?.booking_outcome ?? 'all');
  const [selectedQualityForm, setSelectedQualityForm] = useState(() => initialFilters?.quality_form ?? 'all');
  // Спец-фильтры drill-down (сбрасываются чипами, не имеют своих select'ов).
  const [funnelStage, setFunnelStage] = useState(() => initialFilters?.funnel_stage ?? null);
  const [selectedObjection, setSelectedObjection] = useState(() => initialFilters?.objection ?? null);
  const [selectedNegativeSubCategory, setSelectedNegativeSubCategory] = useState(() => initialFilters?.negative_sub_category ?? null);
  const [criterionFilter, setCriterionFilter] = useState(() => (
    initialFilters?.criterion
      ? { criterion: initialFilters.criterion, maxScore: initialFilters.criterion_max_score ?? 0.5 }
      : null
  )); // { criterion, maxScore }
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [selectedCall, setSelectedCall] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [classificationTypeOptions, setClassificationTypeOptions] = useState(['all']);
  const [filterOptions, setFilterOptions] = useState(null);
  const callsRequestIdRef = useRef(0);

  const callSummary = useMemo(
    () => formatCallSummary(selectedCall?.summary, selectedCall?.bookingOutcomeEvidence),
    [selectedCall?.summary, selectedCall?.bookingOutcomeEvidence]
  );

  const buildFilterParams = useCallback(() => ({
    department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
    employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
    call_classification_type: !IS_PLUS_TENANT && selectedClassificationType !== 'all' ? selectedClassificationType : undefined,
    call_type: selectedCallType !== 'all' ? selectedCallType : undefined,
    alternative_offered: !IS_PLUS_TENANT && selectedAlternativeOffer !== 'all' ? selectedAlternativeOffer : undefined,
    primary_intent: !IS_PLUS_TENANT && selectedPrimaryIntent !== 'all' ? selectedPrimaryIntent : undefined,
    service_area: selectedServiceArea !== 'all' ? selectedServiceArea : undefined,
    booking_outcome: selectedBookingOutcome !== 'all' ? selectedBookingOutcome : undefined,
    quality_form: !IS_PLUS_TENANT && selectedQualityForm !== 'all' ? selectedQualityForm : undefined,
    funnel_stage: funnelStage ?? undefined,
    objection: selectedObjection ?? undefined,
    negative_sub_category: selectedNegativeSubCategory ?? undefined,
    criterion: criterionFilter?.criterion ?? undefined,
    criterion_max_score: criterionFilter?.criterion != null ? criterionFilter?.maxScore : undefined,
    search: searchQuery || undefined,
    date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined
  }), [
    selectedDepartment,
    selectedEmployee,
    selectedClassificationType,
    selectedCallType,
    selectedAlternativeOffer,
    selectedPrimaryIntent,
    selectedServiceArea,
    selectedBookingOutcome,
    selectedQualityForm,
    funnelStage,
    selectedObjection,
    selectedNegativeSubCategory,
    criterionFilter,
    searchQuery,
    dateRange?.from,
    dateRange?.to
  ]);

  const loadCalls = useCallback(async () => {
    const requestId = ++callsRequestIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        ...buildFilterParams(),
        sort: sortConfig.key ?? undefined,
        order: sortConfig.key ? sortConfig.direction : undefined
      };

      const response = await fetchCalls(params);
      if (requestId !== callsRequestIdRef.current) return;
      const items = response.items ?? [];

      setCalls(items);
      setTotal(response.total ?? 0);
    } catch (err) {
      if (requestId !== callsRequestIdRef.current) return;
      console.error(err);
      setError('Не удалось загрузить список звонков.');
    } finally {
      if (requestId === callsRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [page, buildFilterParams, sortConfig]);

  useEffect(() => {
    setPage(1);
  }, [
    dateRange?.from,
    dateRange?.to,
    selectedClassificationType,
    selectedCallType,
    selectedAlternativeOffer,
    selectedDepartment,
    selectedEmployee,
    selectedPrimaryIntent,
    selectedServiceArea,
    selectedBookingOutcome,
    selectedQualityForm,
    funnelStage,
    selectedObjection,
    selectedNegativeSubCategory,
    criterionFilter
  ]);

  useEffect(() => {
    if (initialFilters?.call_type !== undefined) {
      return;
    }

    setSelectedCallType(globalCallType ?? 'all');
  }, [globalCallType, initialFilters]);

  const handleCallTypeChange = useCallback((value) => {
    setSelectedCallType(value);
    onGlobalCallTypeChange?.(value);
  }, [onGlobalCallTypeChange]);

  // Drill-down с других вкладок: применяем переданные фильтры и перезагружаем журнал.
  useEffect(() => {
    if (!initialFilters) {
      return;
    }

    if (initialFilters.department !== undefined) {
      setSelectedDepartment(initialFilters.department ?? 'all');
    }
    if (initialFilters.employee_id !== undefined) {
      setSelectedEmployee(initialFilters.employee_id ?? 'all');
    }
    if (initialFilters.call_classification_type !== undefined) {
      setSelectedClassificationType(initialFilters.call_classification_type ?? 'all');
    }
    if (initialFilters.call_type !== undefined) {
      setSelectedCallType(initialFilters.call_type ?? 'all');
    }
    if (initialFilters.alternative_offered !== undefined) {
      setSelectedAlternativeOffer(initialFilters.alternative_offered ?? 'all');
    }
    if (initialFilters.funnel_stage !== undefined) {
      setFunnelStage(initialFilters.funnel_stage ?? null);
    }
    if (initialFilters.objection !== undefined) {
      setSelectedObjection(initialFilters.objection ?? null);
    }
    if (initialFilters.negative_sub_category !== undefined) {
      setSelectedNegativeSubCategory(initialFilters.negative_sub_category ?? null);
    }
    if (initialFilters.criterion !== undefined) {
      setCriterionFilter(
        initialFilters.criterion
          ? { criterion: initialFilters.criterion, maxScore: initialFilters.criterion_max_score ?? 0.5 }
          : null
      );
    }
    if (initialFilters.primary_intent !== undefined) {
      setSelectedPrimaryIntent(initialFilters.primary_intent ?? 'all');
    }
    if (initialFilters.service_area !== undefined) {
      setSelectedServiceArea(initialFilters.service_area ?? 'all');
    }
    if (initialFilters.booking_outcome !== undefined) {
      setSelectedBookingOutcome(initialFilters.booking_outcome ?? 'all');
    }
    if (initialFilters.quality_form !== undefined) {
      setSelectedQualityForm(initialFilters.quality_form ?? 'all');
    }
    if (initialFilters.search !== undefined) {
      setSearchQuery(initialFilters.search ?? '');
    }

    setPage(1);
    onInitialFiltersHandled?.();
  }, [initialFilters, onInitialFiltersHandled]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) {
      setClassificationTypeOptions(['all']);
      setFilterOptions(null);
      return undefined;
    }

    let cancelled = false;

    const loadFilterOptions = async () => {
      try {
        const response = await fetchCallFilterOptions({
          date_from: format(dateRange.from, 'yyyy-MM-dd'),
          date_to: format(dateRange.to, 'yyyy-MM-dd'),
          call_type: selectedCallType === 'all' ? undefined : selectedCallType
        });

        if (cancelled) {
          return;
        }

        const classificationTypes = ['all', ...(response.classificationTypes ?? [])];

        setClassificationTypeOptions(classificationTypes);
        setFilterOptions(response ?? null);
        if (
          selectedPrimaryIntent !== 'all'
          && !(response?.primaryIntents ?? []).some((option) => option.code === selectedPrimaryIntent)
        ) {
          setSelectedPrimaryIntent('all');
        }
        setSelectedClassificationType((current) => (
          current === 'all' || classificationTypes.includes(current) ? current : 'all'
        ));
      } catch (err) {
        if (!cancelled) {
          console.error(err);
        }
      }
    };

    loadFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [dateRange?.from, dateRange?.to, selectedCallType, selectedPrimaryIntent]);

  useEffect(() => {
    if (!initialCallId) {
      return;
    }

    let cancelled = false;

    fetchCalls({
      call_id: initialCallId,
      limit: 1
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const call = response.items?.[0];
        if (call) {
          setSelectedCall(call);
          setShowDetailsModal(true);
          return;
        }

        setError('Звонок по ссылке не найден.');
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          onInitialCallHandled?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialCallId, onInitialCallHandled]);

  const handleExport = useCallback(() => {
    startExport(buildFilterParams());
  }, [buildFilterParams, startExport]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.key === column) {
        return {
          key: column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return { key: column, direction: 'asc' };
    });
  };

  const sortedCalls = useMemo(() => {
    if (!sortConfig.key) {
      return calls;
    }

    const key = sortConfig.key;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;

    return [...calls].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }, [calls, sortConfig]);

  const callTypeOptions = useMemo(() => {
    const sourceOptions = filterOptions?.callTypes?.length
      ? filterOptions.callTypes
      : getCallTypeOptionsForTenant(TENANT_ID);
    const normalizedOptions = sourceOptions
      .map((option) => ({
        code: option.code?.toString?.() ?? option.value?.toString?.() ?? '',
        label: option.label ?? option.name ?? option.code
      }))
      .filter((option) => option.code);
    const tenantOptions = TENANT_ID === 'electrobike'
      ? normalizedOptions.filter((option) => ['incoming', 'outgoing'].includes(option.code))
      : normalizedOptions;
    const options = [
      { code: 'all', label: 'Все звонки' },
      ...tenantOptions.filter((option) => option.code !== 'all')
    ];

    return [...new Map(options.map((option) => [option.code, option])).values()];
  }, [filterOptions?.callTypes]);

  useEffect(() => {
    if (callTypeOptions.length === 0) {
      return;
    }

    if (!callTypeOptions.some((option) => option.code === selectedCallType)) {
      setSelectedCallType(callTypeOptions[0].code);
    }
  }, [callTypeOptions, selectedCallType]);

  const employeeOptions = useMemo(() => {
    const apiEmployees = (filterOptions?.employees ?? [])
      .map((employee) => ({
        id: employee.code?.toString?.() ?? employee.id?.toString?.() ?? '',
        name: employee.label ?? employee.name ?? employee.fullName ?? employee.code,
        department: employee.department ?? null
      }))
      .filter((employee) => employee.id);

    if (apiEmployees.length > 0) {
      return apiEmployees;
    }

    return employees
      .map((employee) => ({
        id: employee.id?.toString?.() ?? employee.employeeId?.toString?.() ?? '',
        name: employee.name ?? employee.fullName ?? employee.employeeName ?? 'Без имени',
        department: employee.department ?? null
      }))
      .filter((employee) => employee.id);
  }, [employees, filterOptions?.employees]);

  const departmentOptions = useMemo(() => {
    const apiDepartments = (filterOptions?.departments ?? [])
      .map((department) => ({
        id: department.code?.toString?.() ?? department.label?.toString?.() ?? '',
        name: department.label ?? department.code
      }))
      .filter((department) => department.id);
    const departments = apiDepartments.length > 0
      ? apiDepartments
      : [...new Set(employeeOptions.filter((employee) => employee.department).map((employee) => employee.department))]
      .sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' }))
      .map((department) => ({ id: department, name: department }));

    return [
      { id: 'all', name: 'Все отделы' },
      ...departments
    ];
  }, [employeeOptions, filterOptions?.departments]);

  const uniqueEmployees = useMemo(() => {
    const filteredEmployees = selectedDepartment === 'all'
      ? employeeOptions
      : employeeOptions.filter((employee) => employee.department === selectedDepartment);

    return [
      { id: 'all', name: 'Все сотрудники' },
      ...filteredEmployees.map((emp) => ({
        id: emp.id,
        name: emp.name
      }))
    ];
  }, [employeeOptions, selectedDepartment]);

  useEffect(() => {
    if (selectedDepartment !== 'all' && !departmentOptions.some((department) => department.id === selectedDepartment)) {
      setSelectedDepartment('all');
    }
  }, [departmentOptions, selectedDepartment]);

  useEffect(() => {
    if (selectedEmployee !== 'all' && !uniqueEmployees.some((employee) => employee.id === selectedEmployee)) {
      setSelectedEmployee('all');
    }
  }, [selectedEmployee, uniqueEmployees]);

  const handleDepartmentChange = useCallback((department) => {
    setSelectedDepartment(department);
    setSelectedEmployee('all');
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-3">
          <div className="text-left">
            <CardTitle>{texts.title}</CardTitle>
            <CardDescription>{texts.description}</CardDescription>
          </div>
          <div className="grid w-full gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6">
            <div className="relative w-full">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={texts.filters.search}
                value={searchQuery}
                onChange={(event) => {
                  setPage(1);
                  setSearchQuery(event.target.value);
                }}
              />
            </div>

            <Select value={selectedDepartment} onValueChange={handleDepartmentChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Все отделы" />
              </SelectTrigger>
              <SelectContent>
                {departmentOptions.map((department) => (
                  <SelectItem key={department.id} value={department.id}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Сотрудник" />
              </SelectTrigger>
              <SelectContent>
                {uniqueEmployees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!IS_PLUS_TENANT && (
              <Select value={selectedClassificationType} onValueChange={setSelectedClassificationType}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Тип звонка" />
                </SelectTrigger>
                <SelectContent>
                  {classificationTypeOptions.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item === 'all' ? 'Все типы' : formatClassificationType(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={selectedCallType} onValueChange={handleCallTypeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Направление" />
              </SelectTrigger>
              <SelectContent>
                {callTypeOptions.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!IS_PLUS_TENANT && (
              <Select value={selectedAlternativeOffer} onValueChange={setSelectedAlternativeOffer}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Альтернативный товар" />
                </SelectTrigger>
                <SelectContent>
                  {ALTERNATIVE_OFFER_OPTIONS.map((option) => (
                    <SelectItem key={option.code} value={option.code}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {!IS_PLUS_TENANT && (
              <TaxonomySelect
                value={selectedPrimaryIntent}
                onChange={setSelectedPrimaryIntent}
                allLabel="Все темы"
                options={filterOptions?.primaryIntents}
              />
            )}

            <TaxonomySelect
              value={selectedServiceArea}
              onChange={setSelectedServiceArea}
              allLabel="Все услуги"
              options={filterOptions?.serviceAreas}
            />

            <TaxonomySelect
              value={selectedBookingOutcome}
              onChange={setSelectedBookingOutcome}
              allLabel="Все итоги звонка"
              options={filterOptions?.bookingOutcomes}
            />

            {!IS_PLUS_TENANT && (
              <TaxonomySelect
                value={selectedQualityForm}
                onChange={setSelectedQualityForm}
                allLabel="Все формы чек-листа"
                options={filterOptions?.qualityForms}
              />
            )}

            <Button variant="outline" className="w-full" onClick={loadCalls}>
              <Filter className="mr-2 h-4 w-4" />
              Обновить
            </Button>

            <Button variant="outline" className="w-full" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Excel
            </Button>
          </div>
          {(funnelStage || selectedObjection || selectedNegativeSubCategory || criterionFilter) && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-xs text-muted-foreground">Доп. фильтры:</span>
              {funnelStage && (
                <FilterChip
                  label={`Воронка: ${FUNNEL_STAGE_LABELS[funnelStage] ?? funnelStage}`}
                  onRemove={() => setFunnelStage(null)}
                />
              )}
              {selectedObjection && (
                <FilterChip
                  label={`Возражение: ${resolveTaxonomyLabel(filterOptions?.objections, selectedObjection)}`}
                  onRemove={() => setSelectedObjection(null)}
                />
              )}
              {selectedNegativeSubCategory && (
                <FilterChip
                  label={`Алерт: ${NEGATIVE_SUB_CATEGORY_LABELS[selectedNegativeSubCategory] ?? selectedNegativeSubCategory}`}
                  onRemove={() => setSelectedNegativeSubCategory(null)}
                />
              )}
              {criterionFilter && (
                <FilterChip
                  label={`Критерий: ${CRITERION_LABELS[criterionFilter.criterion] ?? criterionFilter.criterion}${criterionFilter.maxScore != null ? ` (оценка ≤ ${criterionFilter.maxScore})` : ''}`}
                  onRemove={() => setCriterionFilter(null)}
                />
              )}
            </div>
          )}
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <CardTitle>Журнал звонков</CardTitle>
          <div className="text-sm text-muted-foreground">
            Страница {page} из {totalPages} &middot; Всего {total.toLocaleString()}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Дата" column="callStartedAt" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Сотрудник" column="employeeName" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead>Клиент</TableHead>
                  <TableHead>Направление</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Жалобы</TableHead>
                  <TableHead>Итог звонка</TableHead>
                  <SortableHead label="Чек-лист" column="checklistScore" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Длительность" column="durationSeconds" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead>Подробнее</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground">
                      Загружаем данные...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && sortedCalls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground">
                      Нет записей для отображения.
                    </TableCell>
                  </TableRow>
                )}
                {sortedCalls.map((call) => (
                  <TableRow key={call.callId} className="[&>td]:align-middle [&>td]:py-3">
                    <TableCell>{formatDate(call.callStartedAt)}</TableCell>
                    <TableCell>{call.employeeName || '—'}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{call.clientName || call.clientPhone || '—'}</div>
                      <div className="text-xs text-muted-foreground">{call.clientCompany || (call.clientName ? call.clientPhone : null)}</div>
                    </TableCell>
                    <TableCell>{formatDirection(call.callType)}</TableCell>
                    <TableCell>{renderPrimaryIntent(call)}</TableCell>
                    <TableCell>{formatComplaints(call)}</TableCell>
                    <TableCell>
                      <CallOutcomeBadge call={call} />
                    </TableCell>
                    <TableCell>{formatChecklistScore(call.checklistScore)}</TableCell>
                    <TableCell>{formatDuration(call.durationSeconds)}</TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => { setSelectedCall(call); setShowDetailsModal(true); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Показано {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} из {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Назад
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Вперёд
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Детали звонка</DialogTitle>
            <DialogDescription>{formatDetailsSubtitle(selectedCall)}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <InfoBlock label="Телефон клиента" value={selectedCall?.clientPhone ?? '—'} />
              <InfoBlock label="Направление" value={formatDirection(selectedCall?.callType)} />
              <InfoBlock label="Услуга" value={selectedCall?.serviceAreaLabel ?? selectedCall?.serviceArea ?? '—'} />
              <InfoBlock label="Итог звонка" value={resolveCallOutcome(selectedCall)} />
              <InfoBlock label="Итог чек-листа 1-й оп." value={formatOperatorTotal(selectedCall?.firstOperatorTotal)} />
            </div>

            {callSummary && (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-foreground">Краткое содержание</h4>
                <div className="rounded-md bg-muted p-3 text-sm italic leading-relaxed text-muted-foreground">
                  «{callSummary}»
                </div>
              </div>
            )}

            {!isInternalCall(selectedCall) && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">Возражения</h4>
                {resolveObjectionLabels(selectedCall).length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {resolveObjectionLabels(selectedCall).map((label) => (
                      <Badge key={label} variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                        {label}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Возражений не зафиксировано.</p>
                )}
              </div>
            )}

            {!isInternalCall(selectedCall) && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">Чек-лист</h4>
                <div className="rounded-md bg-muted p-3 text-sm leading-relaxed space-y-3">
                  {Object.entries(selectedCall?.checklist ?? {}).length === 0 && (
                    <p className="text-muted-foreground">Нет данных чек-листа.</p>
                  )}
                  {Object.entries(selectedCall?.checklist ?? {}).map(([key, item]) => {
                    const { symbol, className } = resolveChecklistScoreStyle(item?.score);
                    const explanation = resolveChecklistExplanation(item);
                    return (
                      <div key={key} className="space-y-1 border-b border-border/50 pb-2 last:border-b-0 last:pb-0">
                        <div className="flex items-center justify-between gap-2">
                          <span>{item?.label ?? key}</span>
                          <span className={`whitespace-nowrap font-medium ${className}`}>{symbol}</span>
                        </div>
                        {explanation && (
                          <p className="pr-8 text-xs leading-relaxed text-muted-foreground">
                            {explanation}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!isInternalCall(selectedCall) && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">Жалобы</h4>
                <div className="rounded-md bg-muted p-3 text-sm leading-relaxed">
                  {formatComplaints(selectedCall)}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground">Прослушать запись</h4>
                {resolveRecordingUrl(selectedCall) && (
                  <Button asChild size="sm" variant="outline">
                    <a href={resolveRecordingUrl(selectedCall)} download>
                      Скачать
                    </a>
                  </Button>
                )}
              </div>
              {resolveRecordingUrl(selectedCall) ? (
                <audio className="w-full" src={resolveRecordingUrl(selectedCall)} controls />
              ) : (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  Запись недоступна
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SortableHead = ({ label, column, sortConfig, onSort }) => {
  const isActive = sortConfig.key === column;
  return (
    <TableHead className="cursor-pointer select-none" onClick={() => onSort(column)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : null}
      </span>
    </TableHead>
  );
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const formatChecklistScore = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '—' : parsed.toFixed(1);
};

const formatEvidence = (value) => {
  if (!value) return '';

  const chunks = String(value)
    .split(/\s*\|\s*/g)
    .map((part) => part.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

  const seen = new Set();
  const unique = [];
  for (const chunk of chunks) {
    const key = chunk.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(chunk);
  }

  return unique.join(' | ');
};

const formatOutcomeEvidence = (value) => {
  const evidence = formatEvidence(value);
  if (!evidence) return '';
  const compact = evidence.replace(/[«»]/g, '').trim();
  if (compact.length <= 260) return compact;
  const firstSentence = compact.split(/[.!?]\s+/u).find((part) => part.length >= 12 && part.length <= 260);
  return firstSentence ?? `${compact.slice(0, 257).trim()}...`;
};

const formatCallSummary = (summary, fallbackEvidence) => {
  const compactSummary = String(summary ?? '').replace(/[«»]/g, '').replace(/\s+/g, ' ').trim();
  if (compactSummary) {
    return compactSummary.length <= 320 ? compactSummary : `${compactSummary.slice(0, 317).trim()}...`;
  }

  return formatOutcomeEvidence(fallbackEvidence);
};

const formatDirection = (value) => {
  if (!value) return '—';
  const normalized = value.trim().toLowerCase();
  if (CALL_TYPE_LABELS[normalized]) return CALL_TYPE_LABELS[normalized];
  if (normalized === 'incoming') return 'Входящий';
  if (normalized === 'outgoing') return 'Исходящий';
  if (normalized === 'internal') return 'Внутренний';
  return value;
};

const formatComplaints = (call) => {
  const categories = Array.isArray(call?.negativeCategories) ? call.negativeCategories : [];
  const subCategories = Array.isArray(call?.negativeSubCategories) ? call.negativeSubCategories : [];
  const all = [...categories, ...subCategories].filter(Boolean);
  if (!all.length) {
    return '—';
  }

  return [
    ...categories.map(formatNegativeCategory),
    ...subCategories.map(formatNegativeSubCategory)
  ].join(', ');
};

const isInternalCall = (call) =>
  call?.callClassificationType === 'internal_or_partner'
  || call?.callType === 'internal_operations';

const resolveChecklistExplanation = (item) => {
  const score = item?.score;
  if (score !== null && score !== undefined && Number(score) > 0) return null;

  const reason = String(item?.reason ?? '').trim();
  if (reason) return reason;

  const applicabilityNote = String(item?.applicabilityNote ?? '').trim();
  return applicabilityNote || null;
};

const renderPrimaryIntent = (call) => {
  const label = call?.primaryIntentLabel ?? call?.primaryIntent;
  if (!label) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  return <Badge variant="outline">{label}</Badge>;
};

const formatDetailsSubtitle = (call) => {
  if (!call) return '';
  const parts = [
    formatDate(call.callStartedAt),
    call.employeeName || '—',
    formatDuration(call.durationSeconds)
  ].filter(Boolean);
  return parts.join(' • ');
};

const TaxonomySelect = ({ value, onChange, allLabel, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full">
      <SelectValue placeholder={allLabel} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{allLabel}</SelectItem>
      {(options ?? []).map((option) => (
        <SelectItem key={option.code} value={option.code}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs text-foreground">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="rounded-full p-0.5 hover:bg-background"
      aria-label="Убрать фильтр"
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

const resolveTaxonomyLabel = (options, code) => {
  if (!code) return '—';
  const match = (options ?? []).find((option) => option.code === code);
  return match?.label ?? code;
};

const resolveObjectionLabels = (call) => {
  const labels = Array.isArray(call?.objectionLabels) ? call.objectionLabels.filter(Boolean) : [];
  if (labels.length > 0) {
    return labels;
  }
  return Array.isArray(call?.objections) ? call.objections.filter(Boolean) : [];
};

const BOOKING_OUTCOME_BADGE_CLASSES = {
  booked: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  waitlist: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300',
  refusal: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  refused: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
};

const resolveCallOutcome = (call) => {
  const outcome = String(call?.outcome ?? '').trim();
  if (outcome && outcome !== '-') {
    return outcome;
  }

  return call?.bookingOutcomeLabel ?? call?.bookingOutcome ?? '—';
};

const CallOutcomeBadge = ({ call }) => {
  const label = resolveCallOutcome(call);
  if (!label || label === '—') {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const badgeClass = BOOKING_OUTCOME_BADGE_CLASSES[call?.bookingOutcome]
    ?? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';

  return <Badge className={badgeClass}>{label}</Badge>;
};

const formatOperatorTotal = (value) => {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  return Number.isNaN(numeric) ? '—' : `${numeric.toFixed(1)}%`;
};

const resolveChecklistScoreStyle = (score) => {
  if (score === null || score === undefined) {
    return { symbol: 'Не применимо', className: 'text-muted-foreground' };
  }

  const numeric = Number(score);
  if (Number.isNaN(numeric)) {
    return { symbol: '—', className: 'text-muted-foreground' };
  }
  if (numeric >= 1) {
    return numeric > 1
      ? { symbol: numeric.toFixed(1), className: 'text-emerald-600' }
      : { symbol: '✓', className: 'text-emerald-600' };
  }
  if (numeric >= 0.5) {
    return { symbol: 'частично', className: 'text-yellow-600' };
  }
  return { symbol: '✗', className: 'text-red-500' };
};

const InfoBlock = ({ label, value }) => (
  <div className="rounded-md border bg-card p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm font-medium text-foreground mt-1">{value ?? '—'}</div>
  </div>
);

const resolveRecordingUrl = (call) => {
  if (!call) return null;
  const trimmed = call.recordingUrl?.trim();
  if (trimmed?.startsWith('http')) return trimmed;
  return getTaskAudioUrl(call.callId);
};

export default CallDetails;
